import { useEffect, useMemo, useRef, useState } from "react";
import type { TransactionsQuery } from "@money/shared";
import {
  useInfiniteTransactions,
  useUpdateTransaction,
  useDeleteTransaction,
  useRestoreTransaction,
} from "../api/transactions";
import { useRenameMerchant } from "../api/merchantRules";
import { useAccounts } from "../api/accounts";
import { useCategories } from "../api/categories";
import { TransactionRow } from "../components/TransactionRow";
import { Loading, ErrorState } from "../components/States";
import { useToast } from "../components/Toast";

const DAY = 86_400_000;
const PAGE_SIZE = 100;
type Preset = "all" | "30" | "90" | "year" | "custom";

export function Transactions() {
  const accounts = useAccounts();
  const categories = useCategories();
  const updateTx = useUpdateTransaction();
  const deleteTx = useDeleteTransaction();
  const restoreTx = useRestoreTransaction();
  const renameMerchant = useRenameMerchant();
  const toast = useToast();
  const categoryNames = categories.data?.categories.map((c) => c.name) ?? [];

  const [account, setAccount] = useState("");
  const [category, setCategory] = useState("");
  const [reviewed, setReviewed] = useState<"" | "0" | "1">("");
  const [preset, setPreset] = useState<Preset>("all");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const range = useMemo<{ from?: number; to?: number }>(() => {
    const now = Date.now();
    if (preset === "30") return { from: now - 30 * DAY };
    if (preset === "90") return { from: now - 90 * DAY };
    if (preset === "year") return { from: new Date(new Date().getFullYear(), 0, 1).getTime() };
    if (preset === "custom") {
      const out: { from?: number; to?: number } = {};
      if (customFrom) {
        const [y, m, d] = customFrom.split("-").map(Number);
        out.from = new Date(y!, m! - 1, d!, 0, 0, 0, 0).getTime();
      }
      if (customTo) {
        const [y, m, d] = customTo.split("-").map(Number);
        out.to = new Date(y!, m! - 1, d!, 23, 59, 59, 999).getTime();
      }
      return out;
    }
    return {};
  }, [preset, customFrom, customTo]);

  const filters = useMemo<TransactionsQuery>(
    () => ({
      limit: PAGE_SIZE,
      ...(account ? { account } : {}),
      ...(category ? { category } : {}),
      ...(reviewed !== "" ? { reviewed: (reviewed === "1" ? 1 : 0) as 0 | 1 } : {}),
      ...(range.from !== undefined ? { from: range.from } : {}),
      ...(range.to !== undefined ? { to: range.to } : {}),
    }),
    [account, category, reviewed, range],
  );

  const q = useInfiniteTransactions(filters);
  const txns = q.data?.pages.flatMap((p) => p.transactions) ?? [];

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && q.hasNextPage && !q.isFetchingNextPage) {
        q.fetchNextPage();
      }
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [q.hasNextPage, q.isFetchingNextPage, q.fetchNextPage]);

  const inputCls = "rounded border border-slate-700 bg-slate-950/40 px-2 py-1 text-sm";

  return (
    <div className="space-y-6">
      <h1 className="text-lg font-semibold">Transactions</h1>

      <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 space-y-3">
        <div className="flex flex-wrap gap-3">
          <label className="flex flex-col gap-1 text-xs text-slate-500">
            Account
            <select value={account} onChange={(e) => setAccount(e.target.value)} className={inputCls}>
              <option value="">All accounts</option>
              {accounts.data?.accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.display_name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-500">
            Category
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls}>
              <option value="">All categories</option>
              {categoryNames.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-500">
            Reviewed
            <select
              value={reviewed}
              onChange={(e) => setReviewed(e.target.value as "" | "0" | "1")}
              className={inputCls}
            >
              <option value="">All</option>
              <option value="1">Reviewed</option>
              <option value="0">Unreviewed</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-slate-500">
            Date range
            <select
              value={preset}
              onChange={(e) => setPreset(e.target.value as Preset)}
              className={inputCls}
            >
              <option value="all">All time</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="year">This year</option>
              <option value="custom">Custom…</option>
            </select>
          </label>
        </div>

        {preset === "custom" && (
          <div className="flex flex-wrap gap-3">
            <label className="flex flex-col gap-1 text-xs text-slate-500">
              From
              <input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className={inputCls}
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-500">
              To
              <input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className={inputCls}
              />
            </label>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4">
        {q.isPending && <Loading className="py-3" />}
        {q.isError && (
          <div className="py-3">
            <ErrorState onRetry={() => q.refetch()} />
          </div>
        )}
        {!q.isPending && !q.isError && txns.length === 0 && (
          <div className="text-slate-500 text-sm py-3">
            No transactions match these filters.
          </div>
        )}
        {txns.map((tx) => (
          <TransactionRow
            key={tx.id}
            tx={tx}
            categories={categoryNames}
            onSave={async (patch) => {
              await updateTx.mutateAsync({ id: tx.id, patch });
              toast("Transaction updated");
            }}
            onRename={async (input) => {
              await renameMerchant.mutateAsync(input);
              toast("Merchant renamed");
            }}
            onDelete={(id) =>
              deleteTx.mutate(id, {
                onSuccess: () =>
                  toast("Transaction deleted", {
                    actionLabel: "Undo",
                    onAction: () => restoreTx.mutate(id),
                    durationMs: 6000,
                  }),
              })
            }
          />
        ))}
        <div ref={sentinelRef} />
        {q.isFetchingNextPage && <Loading label="Loading more…" className="py-3" />}
        {q.hasNextPage && !q.isFetchingNextPage && (
          <div className="py-3">
            <button
              onClick={() => q.fetchNextPage()}
              className="rounded border border-slate-700 hover:border-slate-500 px-3 py-1 text-sm"
            >
              Load more
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
